import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ShieldCheck, Clock, FileCheck, Home as HomeIcon, FileBadge } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeading, CTABand, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const ICONS = [Globe2, FileCheck, HomeIcon, FileBadge];

const COPY = {
  fr: {
    meta: "Services pour la diaspora",
    eyebrow: "Diaspora togolaise",
    h1a: "Le Togo vous manque.",
    h1b: "Les démarches, non.",
    sub: "Vivez à Paris, Montréal, New York ou Abidjan : vos démarches au Togo avancent sans vous déplacer. DOC TOGO est vos yeux, vos mains et votre relais de confiance sur place.",
    servicesEyebrow: "Conçu pour vous",
    servicesTitle: "Votre bureau administratif au Togo.",
    servicesSub: "Quatre familles de services pensées pour les Togolais vivant à l'étranger.",
    services: [
      { title: "Démarches à distance", desc: "Passeport, CNI, casier judiciaire, actes d'état civil : la plupart de vos démarches se font sans voyager. Nous agissons sur place pour vous." },
      { title: "Représentation administrative", desc: "Mandats, dépôts de dossiers, retraits de documents : nous vous représentons auprès des administrations togolaises." },
      { title: "Projets immobiliers & foncier", desc: "Vérification de titres fonciers, due diligence avant achat, suivi de vos projets immobiliers au Togo en toute sécurité." },
      { title: "Légalisation & apostille", desc: "Authentification, légalisation et apostille de vos documents togolais pour usage à l'étranger — et inversement." },
    ],
    contrastEyebrow: "Avant / Après",
    contrastTitle: "Ce que DOC TOGO change pour vous.",
    contrasts: [
      { before: "Prendre un vol pour un acte de naissance", after: "Votre document reçu par courrier sécurisé" },
      { before: "Se fier au hasard des intermédiaires", after: "Un interlocuteur unique, identifié et joignable" },
      { before: "Ne jamais savoir où en est le dossier", after: "Un suivi étape par étape, par email et WhatsApp" },
      { before: "Payer sans savoir pour quoi", after: "Un devis détaillé validé avant tout paiement" },
    ],
    tzBold: "Fuseaux horaires ?",
    tzText: "Notre équipe répond sur WhatsApp et par email, où que vous soyez. Votre référence de dossier vous permet de suivre l'avancement à tout moment.",
    tzCta: "Confier ma démarche",
    ctaTitle: "Votre prochaine démarche au Togo, sans billet d'avion.",
    ctaSub: "Décrivez votre besoin : nous vous répondons sous 24h avec la marche à suivre et un devis clair.",
    imgAlt: "Membres de la diaspora togolaise",
  },
  en: {
    meta: "Diaspora services",
    eyebrow: "Togolese diaspora",
    h1a: "You miss Togo.",
    h1b: "Not the paperwork.",
    sub: "Live in Paris, Montreal, New York or Abidjan: your procedures in Togo move forward without you travelling. DOC TOGO is your eyes, hands and trusted relay on the ground.",
    servicesEyebrow: "Designed for you",
    servicesTitle: "Your administrative office in Togo.",
    servicesSub: "Four service families designed for Togolese living abroad.",
    services: [
      { title: "Remote procedures", desc: "Passport, national ID, police clearance, civil status records: most of your procedures require no travel. We act on the ground for you." },
      { title: "Administrative representation", desc: "Mandates, application filing, document collection: we represent you before Togolese authorities." },
      { title: "Real estate & land projects", desc: "Land title verification, pre-purchase due diligence, monitoring of your real estate projects in Togo, securely." },
      { title: "Legalisation & apostille", desc: "Authentication, legalisation and apostille of your Togolese documents for use abroad — and vice versa." },
    ],
    contrastEyebrow: "Before / After",
    contrastTitle: "What DOC TOGO changes for you.",
    contrasts: [
      { before: "Booking a flight for a birth certificate", after: "Your document delivered by secure mail" },
      { before: "Relying on random middlemen", after: "A single, identified and reachable contact" },
      { before: "Never knowing where your file stands", after: "Step-by-step tracking, by email and WhatsApp" },
      { before: "Paying without knowing what for", after: "A detailed quote approved before any payment" },
    ],
    tzBold: "Time zones?",
    tzText: "Our team replies on WhatsApp and by email, wherever you are. Your case reference lets you track progress at any time.",
    tzCta: "Entrust my request",
    ctaTitle: "Your next procedure in Togo, without a plane ticket.",
    ctaSub: "Describe your need: we reply within 24h with the roadmap and a clear quote.",
    imgAlt: "Members of the Togolese diaspora",
  },
};

export default function Diaspora() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  return (
    <div data-testid="diaspora-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.diaspora}
        imageAlt={c.imgAlt}
      />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading eyebrow={c.servicesEyebrow} title={c.servicesTitle} sub={c.servicesSub} />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {c.services.map((s, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    data-testid={`diaspora-service-${i}`}
                    className="flex h-full gap-6 rounded-3xl border border-brand/10 bg-white p-8 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
                  >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-light text-brand">
                      <Icon className="h-6 w-6" />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <section className="bg-brand-deep py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading dark eyebrow={c.contrastEyebrow} title={c.contrastTitle} />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {c.contrasts.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`diaspora-contrast-${i}`} className="rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-md">
                  <p className="text-sm text-white/45 line-through decoration-crimson/70">{p.before}</p>
                  <div className="mt-3 flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <p className="font-display text-base font-bold text-white lg:text-lg">{p.after}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-3xl border border-gold/25 bg-gold/10 p-8 lg:flex-row lg:items-center">
              <div className="flex items-start gap-4">
                <Clock className="mt-1 h-6 w-6 shrink-0 text-gold" />
                <p className="max-w-2xl text-sm leading-relaxed text-white/75 lg:text-base">
                  <strong className="text-white">{c.tzBold}</strong> {c.tzText}
                </p>
              </div>
              <Link
                to="/contact?categorie=Services%20diaspora"
                data-testid="diaspora-cta-btn"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
              >
                {c.tzCta}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand title={c.ctaTitle} sub={c.ctaSub} />
    </div>
  );
}
